import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import type { AuthTokens, AuthUser, MePermissions } from '@/types/auth';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // epoch ms
  sessionId: string | null;
  user: AuthUser | null;
  /**
   * Effective RBAC grants from GET /me/permissions. `null` = not loaded yet
   * (e.g. a session persisted before permissions were stored) — the RoleGate
   * fetches them before letting the operator in.
   */
  permissions: string[] | null;
  wildcards: string[];
  setSession: (args: {
    tokens: AuthTokens;
    user: AuthUser;
    sessionId?: string | null;
    permissions?: MePermissions | null;
  }) => void;
  setPermissions: (p: MePermissions) => void;
  setTokens: (tokens: AuthTokens) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

function computeExpiry(token: string, expiresIn?: number): number | null {
  if (expiresIn && Number.isFinite(expiresIn)) {
    return Date.now() + expiresIn * 1000;
  }
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

// localStorage keys mirror the reference apps so a manual migration is trivial.
const STORAGE_KEY = 'ticketbox_auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      sessionId: null,
      user: null,
      permissions: null,
      wildcards: [],

      setSession: ({ tokens, user, sessionId, permissions }) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: computeExpiry(tokens.accessToken, tokens.expiresIn),
          sessionId: sessionId ?? null,
          user,
          permissions: permissions ? [...permissions.permissions] : null,
          wildcards: permissions ? [...permissions.wildcards] : [],
        }),

      setPermissions: (p) =>
        set({ permissions: [...p.permissions], wildcards: [...p.wildcards] }),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: computeExpiry(tokens.accessToken, tokens.expiresIn),
        }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          sessionId: null,
          user: null,
          permissions: null,
          wildcards: [],
        }),

      isAuthenticated: () => {
        const { accessToken, refreshToken } = get();
        return Boolean(accessToken && refreshToken);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        expiresAt: s.expiresAt,
        sessionId: s.sessionId,
        user: s.user,
        // Persisted so a reload (or an offline reboot) keeps the gate decision;
        // RoleGate re-checks against the server in the background.
        permissions: s.permissions,
        wildcards: s.wildcards,
      }),
    },
  ),
);

/** Non-React accessor for interceptors. */
export const authStore = {
  get: () => useAuthStore.getState(),
};
